<template>
  <section
    :class="cssClass"
    v-if="subscriptionPlan && subscriptionPlan.trialDays > 0">
    <div class="trial-duration-explanation__container">
      <div
        class="trial-duration-explanation__headline trial-duration-explanation__headline--has-subheader"
        v-html="headline" />
      <div
        class="trial-duration-explanation__subheader">
        {{ subheadline }}
        <mark
          class="trial-duration-explanation__books-and-podcasts"
          v-html="booksAndPodcasts" />
      </div>

      <trial-timeline-with-reminder-step :plan="subscriptionPlan" />

      <div class="trial-duration-explanation__try-blinkist">
        <trial-message
          :signup-route="signupRoute"
          :cta-copy="ctaCopy"
          :cta-js-class="ctaJsClass"
          :piwik-category="piwikCategory"
          :plan="subscriptionPlan"
          :redirect-to-checkout-page="redirectToCheckoutPage"
          :logged-in-user-redirect="loggedInUserRedirect"
          :i18n-key="localeI18nKey" />
      </div>
    </div>
  </section>
</template>

<script>
import TrialTimelineWithReminderStep from "@/features/trial-duration-explanation/components/trial-timeline-with-reminder-step.vue";
import TrialMessage from "@/features/trial-duration-explanation/components/trial-message";
import globals from "@/utils/globals";
import { mapGetters } from "vuex";

export default {
  name: "TrialDurationExplanation",
  components: {
    TrialMessage,
    TrialTimelineWithReminderStep
  },
  props: {
    signupRoute: {
      type: String,
      required: false,
      default: ""
    },
    ctaCopy: {
      type: String,
      default: null
    },
    ctaJsClass: {
      type: String,
      default: null
    },
    piwikCategory: {
      type: String,
      default: "body-20200409-trial_duration_explanation"
    },
    subscriptionPlan: {
      validator: prop => typeof prop === "object" || prop === null,
      default: null,
      required: true
    },
    redirectToCheckoutPage: {
      type: Boolean,
      default: false
    },
    loggedInUserRedirect: {
      type: String,
      default: null
    },
    modifiers: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    ...mapGetters("plans", ["libraryTotalWorth"]),
    localeI18nKey() {
      return "smallInvestment";
    },
    headline() {
      if (this.$te(`${this.localeI18nKey}.headline`)) {
        return this.$t(`${this.localeI18nKey}.headline`, { totalBooks: globals.totalBooks });
      }
      return this.$t(`${this.localeI18nKey}.headline`);
    },
    subheadline() {
      return this.$t(`${this.localeI18nKey}.subheadline`);
    },
    booksAndPodcasts() {
      return this.$t(`${this.localeI18nKey}.booksAndPodcasts`, { totalBooks: globals.totalBooks });
    },
    cssClass() {
      const params = {
        "trial-duration-explanation": true,
        "py-6 m:py-8 l:py-12": true
      };

      this.modifiers.forEach(modifier => {
        params[`trial-duration-explanation--${modifier}`] = true;
      });

      return params;
    }
  },
  i18n: {
    messages: {
      en: {
        smallInvestment: {
          headline: "A small investment, an incredible growth opportunity",
          subheadline: "Get access to powerful ideas from ",
          booksAndPodcasts: "more than {totalBooks} nonfiction books and podcasts."
        }
      },
      de: {
        smallInvestment: {
          headline: "Du investierst in deine Entwicklung",
          subheadline: "Blinkist lohnt sich: Du erhältst Zugang zu den wichtigsten Ideen aus ",
          booksAndPodcasts: "über {totalBooks} Sachbüchern und Podcasts."
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.trial-duration-explanation {
  @include font-family(sans-serif);

  display: flex;
  align-items: flex-end;

  &__container {
    @include container;
  }

  &__headline {
    @include font-style((xs: h3, s: h2));

    width: 100%;
    margin-bottom: $spacing-32;

    text-align: center;

    br { /* stylelint-disable-line selector-nested-pattern */
      @include media(min-width, m) {
        display: none;
      }
    }

    @include media(min-width, m) {
      margin-bottom: $spacing-48;
    }
  }

  &__headline--has-subheader {
    margin-bottom: $spacing-24;
  }

  &__subheader {
    @include font-style(p0);

    margin-bottom: $spacing-32;

    font-weight: normal;

    @include media(min-width, m) {
      text-align: center;
    }
  }

  &__books-and-podcasts {
    @include highlight;

    font-weight: bold;
  }

  &--pale-mint-grey-bg {
    background-color: $pale-mint-grey;
  }
}
</style>
